import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, AlertCircle } from 'lucide-react';
import ReferralAnalyzer from '@/lib/FraudChecker';
import CSVCleaner from '@/lib/CSVCleaner';

const NEWSLETTER_REVENUE = {
  'the news': 0.12,
  'the bizness': 0.17,
  'the champs': 0.03,
  'the stories': 0.05,
  'health times': 0.36,
  'GoGet': 0.03,
  'rising': 0.05
};

const ACQUISITION_SOURCES = {
  Meta: (source: string) => source.toLowerCase().includes('meta') || source.toLowerCase().includes('facebook'),
  Google: (source: string) => source.toLowerCase().includes('google'),
  TikTok: (source: string) => source.toLowerCase().includes('tiktok'),
  Referral: (source: string) => source.toLowerCase().includes('referral'),
  Others: () => true // Catch all
};

export default function UploadAnalysis() {
  const [selectedNewsletter, setSelectedNewsletter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<Record<string, { mean: number; median: number }> | null>(null);

  const calculateLTV = (opens: number, revenuePerOpen: number) => {
    return opens * revenuePerOpen;
  };

  const processCSV = async (file: File) => {
    if (!selectedNewsletter) {
      setError('Por favor, selecione a newsletter antes.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const text = await file.text();
      const rows = text.split('\n').map(row => row.split(','));
      const headers = rows[0].map(h => h.trim().toLowerCase());
      
      // Get column indexes
      const sourceIndex = headers.indexOf('acquisition_source');
      const opensIndex = headers.indexOf('total_unique_opened');
      
      if (sourceIndex === -1 || opensIndex === -1) {
        throw new Error('Required columns not found in CSV');
      }

      // Group data by acquisition source
      const ltvBySource: Record<string, number[]> = {};
      
      rows.slice(1).forEach(row => {
        if (!row[sourceIndex] || !row[opensIndex]) return;
        
        const source = row[sourceIndex].trim();
        const opens = parseInt(row[opensIndex]) || 0;
        const ltv = calculateLTV(opens, NEWSLETTER_REVENUE[selectedNewsletter as keyof typeof NEWSLETTER_REVENUE]);

        // Determine which source category this belongs to
        for (const [category, matcher] of Object.entries(ACQUISITION_SOURCES)) {
          if (matcher(source)) {
            if (!ltvBySource[category]) ltvBySource[category] = [];
            ltvBySource[category].push(ltv);
            break;
          }
        }
      });

      // Calculate statistics
      const results: Record<string, { mean: number; median: number }> = {};
      
      Object.entries(ltvBySource).forEach(([source, values]) => {
        if (!values.length) return;
        
        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        const sortedValues = [...values].sort((a, b) => a - b);
        const median = sortedValues.length % 2 === 0
          ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
          : sortedValues[Math.floor(sortedValues.length / 2)];
        
        results[source] = { mean, median };
      });

      setResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro processando o arquivo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 bg-white dark:bg-gray-900">
      <Card className="border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-gray-100">Análise de LTV</CardTitle>
          <CardDescription className="text-gray-500 dark:text-gray-400">Carregue dados de assinantes para analisar LTV por fonte de aquisição</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Select value={selectedNewsletter} onValueChange={setSelectedNewsletter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Selecione a newsletter" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(NEWSLETTER_REVENUE).map(newsletter => (
                  <SelectItem key={newsletter} value={newsletter}>
                    {newsletter}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-4">
              <Button
                disabled={!selectedNewsletter || loading}
                onClick={() => document.getElementById('csvInput')?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Carregar CSV
              </Button>
              <input
                id="csvInput"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) processCSV(file);
                }}
              />
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100" />}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-gray-500 dark:text-gray-400">{error}</AlertDescription>
              </Alert>
            )}

            {results && (
              <div className="mt-6 space-y-4">
                {Object.entries(results).map(([source, stats]) => (
                  <div key={source} className="p-4 border rounded-lg border-gray-200 dark:border-gray-700">
                    <h3 className="font-medium text-lg mb-2 text-gray-900 dark:text-gray-100">{source}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Média LTV</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                          R$ {stats.mean.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Mediana LTV</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                          R$ {stats.median.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      <ReferralAnalyzer />
      <CSVCleaner />
    </div>
  );
}